import React, { useMemo, useCallback, useState } from 'react';
import { Modal, Form, Button, Alert, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { v4 as uuidv4 } from 'uuid';
import { SidcBuilder } from '../SidcBuilder';
import { useUnitModalState } from './useUnitModalState';
import { UnitModalProps, UnitSubmitData } from './UnitModal.types';

/**
 * Unified modal component for creating and editing military units
 * Supports both Map and ORBAT contexts
 */
export const UnitModal: React.FC<UnitModalProps> = ({
    show,
    onHide,
    context,
    mode = 'create',
    editingUnit,
    position,
    parentUnit,
    onSubmit,
    factionAffiliationName,
    lockIdentity = false,
    showNameField = context === 'orbat',
    // A unit must be identifiable in the ORBAT tree, so its name is mandatory.
    // A marker is often just a symbol dropped on the map ("mine here") with
    // nothing worth calling it, so its name is optional — blank simply means the
    // symbol draws no designation label.
    requireName = context === 'orbat',
    showTextModifiers = true,
    hideIdentity = false,
    filterEchelonByParent = false,
    limitStatusOptions = false,
    simplifyHqtfdToDummy = false,
    simplifyStatusToPlanned = false,
    isLoading = false,
    error = null,
}) => {
    const state = useUnitModalState({
        show,
        mode,
        context,
        editingUnit,
        factionAffiliationName,
        parentUnit,
        lockIdentity,
    });

    const [isGlobal, setIsGlobal] = useState(false);

    // Determine modal title based on context and mode
    const modalTitle = useMemo(() => {
        if (mode === 'edit' && editingUnit) {
            return `Edit Unit: ${editingUnit.name || 'Unnamed'}`;
        }
        if (context === 'orbat') {
            return parentUnit
                ? `Add Subordinate to "${parentUnit.name}"`
                : 'Add Root Unit';
        }
        return 'Add Marker';
    }, [mode, editingUnit, context, parentUnit]);

    // Handle modal close
    const handleClose = useCallback(() => {
        state.resetForm();
        setIsGlobal(false);
        onHide();
    }, [state, onHide]);

    // Handle form submission
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const form = event.currentTarget;

        // Validate form for ORBAT context (name is required)
        if (showNameField && form.checkValidity() === false) {
            event.stopPropagation();
            state.setValidated(true);
            return;
        }

        state.setValidated(true);

        try {
            const submitData: UnitSubmitData = {
                sidc: state.sidc,
            };

            // The name is the entity's single identity field, so it is
            // submitted in every context that collects it — markers included.
            // It used to be set only for ORBAT units, which is why map markers
            // fell back to labelling themselves with a raw SIDC string.
            // Submitted even when blank: for a marker that is a meaningful
            // value ("no label"), and on edit it is how a name gets cleared.
            if (showNameField) {
                submitData.name = state.name.trim();
            }

            // Add context-specific data
            if (context === 'map') {
                submitData.id = uuidv4();
                submitData.isGlobal = isGlobal;
                if (position) {
                    submitData.position = position;
                }
                // Only include markerProperties if there are values
                if (Object.keys(state.markerProperties).length > 0) {
                    submitData.markerProperties = state.markerProperties;
                }
            } else if (context === 'orbat') {
                if (mode === 'edit' && editingUnit) {
                    submitData.id = editingUnit.id;
                }
                submitData.upper_unit_id = parentUnit?.id || null;
                submitData.isPubliclyJoinable = state.isPubliclyJoinable;
                // Include markerProperties if text modifiers are shown and have values
                if (showTextModifiers && Object.keys(state.markerProperties).length > 0) {
                    submitData.markerProperties = state.markerProperties;
                }
            }

            await onSubmit(submitData);
            handleClose();
        } catch (err) {
            console.error(mode === 'edit' ? 'Unit update failed:' : 'Unit creation failed:', err);
            // Error state is managed by parent component for ORBAT context
        }
    };

    // Determine submit button text
    const submitButtonText = useMemo(() => {
        if (isLoading) {
            return mode === 'edit' ? 'Saving...' : 'Creating...';
        }
        if (context === 'map') {
            return 'Add Marker';
        }
        return mode === 'edit' ? 'Save Changes' : 'Create Unit';
    }, [isLoading, mode, context]);

    return (
        <Modal show={show} onHide={handleClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{modalTitle}</Modal.Title>
            </Modal.Header>
            <Form noValidate validated={state.validated} onSubmit={handleSubmit}>
                <Modal.Body>
                    {/* Error Alert */}
                    {error && (
                        <Alert variant="danger" className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                            {error}
                        </Alert>
                    )}

                    {/* Global Marker Checkbox (Map context) */}
                    {context === 'map' && (
                        <Form.Group className="mb-3" controlId="globalMarker">
                            <Form.Check
                                type="checkbox"
                                label="Global marker (visible to all factions)"
                                checked={isGlobal}
                                onChange={(e) => setIsGlobal(e.target.checked)}
                                disabled={isLoading}
                            />
                        </Form.Group>
                    )}

                    {/* Name field — mandatory for units, optional for markers */}
                    {showNameField && (
                        <Form.Group className="mb-3" controlId="unitName">
                            <Form.Label>
                                {requireName ? 'Unit Name' : 'Name (optional)'}
                            </Form.Label>
                            <Form.Control
                                type="text"
                                placeholder={
                                    requireName
                                        ? 'Enter unit name (e.g., Alpha Company, 1st Platoon)'
                                        : 'Leave blank for an unlabelled symbol'
                                }
                                value={state.name}
                                onChange={(e) => state.setName(e.target.value)}
                                required={requireName}
                                minLength={requireName ? 1 : undefined}
                                maxLength={127}
                                disabled={isLoading}
                                autoFocus
                            />
                            <Form.Control.Feedback type="invalid">
                                Please enter a unit name (1-127 characters).
                            </Form.Control.Feedback>
                        </Form.Group>
                    )}

                    {/* Publicly Joinable Checkbox (ORBAT context) */}
                    {context === 'orbat' && (
                        <Form.Group className="mb-3" controlId="unitIsPubliclyJoinable">
                            <div className="d-flex align-items-center">
                                <Form.Check
                                    type="checkbox"
                                    id="unitIsPubliclyJoinableCheck"
                                    label="Allow public players to claim this unit"
                                    checked={state.isPubliclyJoinable}
                                    onChange={(e) =>
                                        state.setIsPubliclyJoinable(e.target.checked)
                                    }
                                    disabled={isLoading}
                                />
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="publicly-joinable-tooltip">
                                            When the parent event is public AND ongoing,
                                            this unit appears in the in-app lobby for any
                                            player to claim.
                                        </Tooltip>
                                    }
                                >
                                    <Button
                                        variant="link"
                                        className="ms-2 p-0 border-0 text-muted"
                                        tabIndex={0}
                                        aria-label="More info about publicly joinable units"
                                        style={{ lineHeight: 1 }}
                                    >
                                        <FontAwesomeIcon icon={faCircleInfo} />
                                    </Button>
                                </OverlayTrigger>
                            </div>
                        </Form.Group>
                    )}

                    {/* SIDC Builder */}
                    <SidcBuilder
                        sidc={state.sidc}
                        onSidcChange={state.setSidc}
                        markerProperties={state.markerProperties}
                        previewName={state.name}
                        onMarkerPropertiesChange={showTextModifiers ? state.setMarkerProperties : undefined}
                        lockedIdentity={state.lockedIdentityValue}
                        disabled={isLoading}
                        showTextModifiers={showTextModifiers}
                        hideIdentity={hideIdentity}
                        parentEchelon={filterEchelonByParent ? state.parentEchelonValue : undefined}
                        limitStatusOptions={limitStatusOptions}
                        simplifyHqtfdToDummy={simplifyHqtfdToDummy}
                        simplifyStatusToPlanned={simplifyStatusToPlanned}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={isLoading}>
                        {isLoading && (
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                        )}
                        {submitButtonText}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default UnitModal;
